import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Text,
  TextInput,
  TrashIcon,
} from '@razorpay/blade/components';
import AutoCompleteV2 from 'components/ui/AutoCompleteV2/AutoCompleteV2';
import type { OTFormulaCard } from 'api/gql-utils/overtime/types';
import type { LocalEntry } from './types';
import { formatFormulaDescription } from './utils';
import { AmountCell } from './AmountCell';
import { FORMULA_RATE_UNIT } from 'components/Settings/AttendanceAndLeavesSettings/constants';

type OTEntryRowProps = {
  row: LocalEntry;
  formulas: OTFormulaCard[];
  usedFormulaIds: Set<string>;
  onUpdate: (patch: Partial<LocalEntry>) => void;
  onRemove: () => void;
  onCalculate: () => void;
  isCalculating: boolean;
  rowIndex: number;
};

export const OTEntryRow = ({
  row,
  formulas,
  usedFormulaIds,
  onUpdate,
  onRemove,
  onCalculate,
  isCalculating,
  rowIndex,
}: OTEntryRowProps) => {
  const selectedFormula = formulas.find((f) => f.id === row.otFormulaCardId);
  const isDayUnit = selectedFormula?.rateUnit === FORMULA_RATE_UNIT.per_day;

  const primaryOvertimeValue = isDayUnit ? row.totalOtDays : row.totalOtHours; //based on formula unit, we will show either days or hours in the first input field
  const numericPrimaryOvertimeValue = parseFloat(primaryOvertimeValue) || 0;
  const numericDays = parseFloat(row.totalOtDays) || 0;
  const hasDailyAllowance = (selectedFormula?.dailyAllowance ?? 0) > 0;

  const hasOvertimeHoursOrDays =
    Boolean(selectedFormula) &&
    numericPrimaryOvertimeValue > 0 &&
    (!hasDailyAllowance || numericDays > 0);

  const displayedAmount = row.isManuallyEdited
    ? row.overrideAmount ?? 0
    : row.calculatedAmount ?? 0;

  const [amountInput, setAmountInput] = useState<string>(
    displayedAmount ? displayedAmount.toFixed(2) : '',
  );
  useEffect(() => {
    if (!row.isManuallyEdited) {
      setAmountInput(displayedAmount ? displayedAmount.toFixed(2) : '');
    }
  }, [displayedAmount, row.isManuallyEdited]);

  const onFormulaChange = (card: OTFormulaCard) => {
    onUpdate({
      otFormulaCardId: card.id,
      totalOtHours: '',
      totalOtDays: '',
      calculatedAmount: null,
      hasError: false,
    });
  };

  const onClearFormula = () => {
    onUpdate({
      otFormulaCardId: null,
      totalOtHours: '',
      totalOtDays: '',
      calculatedAmount: null,
    });
  };

  const onPrimaryOvertimeValueChange = ({ value }: { name?: string; value?: string }) => {
    const cleaned = (value ?? '').replace(/[^0-9.]/g, '');
    const field = isDayUnit ? 'totalOtDays' : 'totalOtHours';
    onUpdate({
      [field]: cleaned,
      ...(row.isManuallyEdited ? {} : { calculatedAmount: null }),
      hasError: false,
    });
  };

  const onDaysChange = ({ value }: { name?: string; value?: string }) => {
    const cleaned = (value ?? '').replace(/[^0-9.]/g, '');
    onUpdate({
      totalOtDays: cleaned,
      ...(row.isManuallyEdited ? {} : { calculatedAmount: null }),
    });
  };

  const onAmountChange = ({ value }: { name?: string; value?: string }) => {
    const cleaned = (value ?? '').replace(/[^0-9.]/g, '');
    setAmountInput(cleaned);
    const parsed = parseFloat(cleaned);
    onUpdate({
      isManuallyEdited: true,
      overrideAmount: Number.isFinite(parsed) ? parsed : 0,
    });
  };

  const onReCalculate = () => {
    onUpdate({ isManuallyEdited: false, overrideAmount: null, calculatedAmount: null });
  };

  const visibleFormulas = formulas.filter(
    (card) => card.id === row.otFormulaCardId || !usedFormulaIds.has(card.id),
  );

  const unitLabel = selectedFormula?.rateUnit === 'per_day' ? 'days' : 'hrs';
  const showFormulaError = row.hasError && !row.otFormulaCardId;
  const showHoursError = row.hasError && row.otFormulaCardId && !numericPrimaryOvertimeValue;
  const showDailyAllowanceError =
    row.hasError && hasDailyAllowance && !(parseFloat(row.totalOtDays) > 0);

  return (
    <TableRow item={row}>
      <TableCell>
        <Box>
          <AutoCompleteV2
            options={visibleFormulas}
            itemToKey={(card: OTFormulaCard) => card.id}
            itemToLabel={(card: OTFormulaCard) => card.name}
            itemToDescription={(card: OTFormulaCard) => formatFormulaDescription(card)}
            name={`overtime-formula-${rowIndex}`}
            value={row.otFormulaCardId ?? ''}
            placeholder="Select a formula…"
            onChange={onFormulaChange}
            onClearSelection={onClearFormula}
            errorText={showFormulaError ? 'Select a formula' : undefined}
            validationState={showFormulaError ? 'error' : 'none'}
          />
        </Box>
      </TableCell>

      <TableCell>
        <Box display="flex" flexDirection="column" gap="spacing.1">
          <Box display="flex" alignItems="center" gap="spacing.2">
            <Box width="80px">
              <TextInput
                accessibilityLabel={isDayUnit ? 'Overtime days' : 'Overtime hours'}
                name={`overtime-primary-${rowIndex}`}
                placeholder="0"
                value={primaryOvertimeValue}
                onChange={onPrimaryOvertimeValueChange}
                isDisabled={!selectedFormula}
                validationState={showHoursError ? 'error' : 'none'}
                errorText={showHoursError ? `Enter ${unitLabel}` : undefined}
              />
            </Box>
            <Text size="small" color="surface.text.gray.muted">
              {unitLabel}
            </Text>
            {hasDailyAllowance && (
              <>
                <Text size="small" color="surface.text.gray.muted">
                  over
                </Text>
                <Box width="80px">
                  <TextInput
                    accessibilityLabel="Overtime days"
                    name={`overtime-days-${rowIndex}`}
                    placeholder="0"
                    value={row.totalOtDays}
                    onChange={onDaysChange}
                    isDisabled={!selectedFormula}
                    validationState={showDailyAllowanceError ? 'error' : 'none'}
                    errorText={showDailyAllowanceError ? 'Enter days' : undefined}
                  />
                </Box>
                <Text size="small" color="surface.text.gray.muted">
                  days
                </Text>
              </>
            )}
          </Box>
        </Box>
      </TableCell>

      <TableCell>
        <AmountCell
          row={row}
          amountValue={amountInput}
          onAmountChange={onAmountChange}
          onReCalculate={onReCalculate}
          isCalculating={isCalculating}
          hasOvertimeHoursOrDays={hasOvertimeHoursOrDays}
          onCalculate={onCalculate}
        />
      </TableCell>
      <TableCell>
        {(row.isNew || row.source === 'manual') && (
          <IconButton
            size="small"
            emphasis="subtle"
            icon={TrashIcon}
            onClick={onRemove}
            accessibilityLabel="Remove overtime entry"
          />
        )}
      </TableCell>
    </TableRow>
  );
};
